import React from 'react';
import _ from 'lodash';

import clsx from 'classnames';
import { makeStyles } from '@mui/styles';
import { useQueryClient } from '@tanstack/react-query';
import CbButton from '../../../../../components/Buttons/CbButton';
import { Modal } from '../../../../../components/modals/v2/helpers/v2.modal.helpers';
import { InvalidateQuotesConfig } from '../../../../../components/modals/InvalidateQuotes';
import { QuestionCard } from '../../../../renewals/agency/P100/RenewalComponents';
import { AccountDetailsForm } from '../../../../../components/accounts/AccountDetailsForm';
import { AccountOrgContactForm } from '../../../../../components/accounts/AccountOrganizationContactForm';
import AccountP100SecurityQuestionsForm from '../../../../../components/accounts/AccountP100SecurityQuestionsForm';
import { AccountDetailsFields } from '../../../../../components/accounts/fields/AccountDetailsFields';
import { AccountOrgContactDefaultFields } from '../../../../../components/accounts/fields/AccountOrganizationContactDefaultFields';
import { P100SecurityQuestionsFields } from '../../../../../components/accounts/fields/P100SecurityQuestionsFields';
import {
  updateOrgProfile,
  updateSecurityQuestions,
  verifyOrgProfile,
} from '../../../../../accounts/AccountService';
import { actionOnCustomer } from '../../../../../inbox/QuotesService';
import { manageAPIError } from '../../../../../utils';
import {
  standaridizeQuoteDetails,
  removeAttestCheck,
} from '../../../../renewals/renewal.utils';
import CustomerRenewalAttestSubmit from './CustomerRenewalAttestSubmit';
import useNotifications from '../../../../../_lib/notifications/useNotifications';

const CustomerPrime100AccountProfileWithRenewalChanges = ({
  quote,
  onMovingAhead,
}: any) => {
  const queryClient = useQueryClient();
  const classes = styles();
  const notifications = useNotifications();

  const standaridizeQuoteData = React.useMemo(
    () => standaridizeQuoteDetails(quote.rawQuote),
    [quote]
  );

  const orgContactDefaultValues = React.useMemo(
    () => defaultOrganizationValues(standaridizeQuoteData),
    [standaridizeQuoteData]
  );

  function handleAccountDetails(formData: any) {
    return handleVerification(
      quote.rawQuote,
      { accountId: quote.rawQuote.accountId, ...formData },
      'firmo',
      notifications
    ).then((response: any) => {
      if (response) {
        return updateOrgProfile(
          {},
          {
            ...response,
          }
        )
          .then(() => {
            removeAttestCheck();
            queryClient.invalidateQueries(['renewal-quote']);
            notifications.success('Quote Details Updated Successfully.');
          })
          .catch((error: any) => {
            const errorResponse = manageAPIError(error);
            notifications.error(errorResponse);
          });
      }
    });
  }

  function handleOrgContact(formData: any) {
    return actionOnCustomer(
      {},
      {
        quoteId: quote.rawQuote.id,
        accountName: _.get(quote.rawQuote, 'firmographicData.name'),
        agentEmail: _.get(quote.rawQuote, 'agentEmail'),
        agentFirstName: _.get(quote.rawQuote, 'agentFirstName'),
        agentLastName: _.get(quote.rawQuote, 'agentLastName'),
        agentPhone: _.get(quote.rawQuote, 'agentPhone'),
        customerFirstName: _.get(formData, 'contactFirstName'),
        customerLastName: _.get(formData, 'contactLastName'),
        customerPhone: _.get(formData, 'contactPhone'),
        customerEmail: _.get(formData, 'contactEmail'),
      }
    )
      .then(() => {
        removeAttestCheck();
        queryClient.invalidateQueries(['renewal-quote']);
        notifications.success(
          'Organization Contact has been updated successfully.'
        );
      })
      .catch((error: any) => {
        const errorResponse = manageAPIError(error);
        notifications.error(errorResponse);
      });
  }

  function handleSecurityQuestions(formData: any) {
    return handleVerification(
      quote.rawQuote,
      { accountId: quote.rawQuote.accountId, ...formData },
      'questions',
      notifications
    ).then((response: any) => {
      if (response) {
        return updateSecurityQuestions({ isRenewal: true }, { ...response })
          .then(() => {
            removeAttestCheck();
            queryClient.invalidateQueries(['renewal-quote']);
            notifications.success('Security Assessment Updated Successfully.');
          })
          .catch((error: any) => {
            const errorResponse = manageAPIError(error);
            notifications.error(errorResponse);
          });
      }
    });
  }

  return (
    <div>
      <AccountDetailsForm
        defaultValues={standaridizeQuoteData}
        handleOnSubmit={handleAccountDetails}
      >
        {() => {
          return (
            <QuestionCard>
                <div className="flex--spaced">
                  <h3 className={clsx('contrast-text', classes.miniheading)}>
                    Account Information
                  </h3>
                </div>
                <AccountDetailsFields />
                <div className={classes.buttonLayout}>
                  <CbButton
                    form="accountDetails"
                    styleName="ctaButton"
                    buttonText="save"
                    type="submit"
                  />
                </div>
              </QuestionCard>
          );
        }}
      </AccountDetailsForm>

      <AccountOrgContactForm
        formName=""
        defaultValues={orgContactDefaultValues}
        handleOnSubmit={handleOrgContact}
      >
        {() => {
          return (
            <QuestionCard>
                <div className="flex--spaced">
                  <h3 className={clsx('contrast-text', classes.miniheading)}>
                    Organization Contact
                  </h3>
                </div>
                <AccountOrgContactDefaultFields />
                <div className={classes.buttonLayout}>
                  <CbButton
                    form="orgContact"
                    styleName="ctaButton"
                    buttonText="save"
                    type="submit"
                  />
                </div>
              </QuestionCard>
          );
        }}
      </AccountOrgContactForm>
      <AccountP100SecurityQuestionsForm
        formName=""
        handleOnSubmit={handleSecurityQuestions}
        defaultValues={standaridizeQuoteData}
      >
        {() => {
          return (
            <QuestionCard>
                <div className="flex--spaced">
                  <h3 className={clsx('contrast-text', classes.miniheading)}>
                    Security Questions
                  </h3>
                </div>
                <P100SecurityQuestionsFields />
                <div className={classes.buttonLayout}>
                  <CbButton
                    form="P100SecQuestions"
                    styleName="ctaButton"
                    buttonText="save"
                    type="submit"
                  />
                </div>
              </QuestionCard>
          );
        }}
      </AccountP100SecurityQuestionsForm>
      <CustomerRenewalAttestSubmit
        data={quote.formattedQuote}
        onMovingAhead={onMovingAhead}
      />
    </div>
  );
};

function handleVerification(
  quote: any,
  payload: any,
  update: any,
  notifications: any
) {
  return verifyOrgProfile({ renewal: true }, { ...payload })
    .then((resp: any) => {
      const { WillInvalidateQuotes } = resp.data;
      if (WillInvalidateQuotes) {
        Modal.show(InvalidateQuotesConfig, {
          data: { update, data: { ...payload } },
          config: {
            title: `Account Changes for ${_.get(
              quote,
              'firmographicData.name',
              'Renewals'
            )}`,
          },
        });
      } else {
        return payload;
      }
    })
    .catch((error: any) => {
      const errorResponse = manageAPIError(error);
      notifications.error(errorResponse);
    });
}

function defaultOrganizationValues(standaridizeQuoteData: any) {
  return {
    contactFirstName: _.get(standaridizeQuoteData, 'policyContactFirstName'),
    contactLastName: _.get(standaridizeQuoteData, 'policyContactLastName'),
    contactPhone: _.get(standaridizeQuoteData, 'policyContactPhone'),
    contactEmail: _.get(standaridizeQuoteData, 'policyContactEmail'),
  };
}

const styles = makeStyles(({ config, palette }) => ({
  backdrop: {
    backgroundColor: palette.background.paper,
    color: palette.common.darkGreyActive,
  },
  additionalInsuredCard: {
    padding: '2.08rem',
  },
  bottomCardPadding: {
    margin: '2.08rem',
    color: palette.background.modal,
    backgroundColor: palette.background.modal,
  },
  mainContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: '2.08rem !important',
  },
  buttonLayout: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '2.85rem',
    paddingBottom: '2.08rem',
  },
  miniheading: {
    fontWeight: config.weights.bold,
    fontSize: '1.66rem',
    paddingLeft: '2.08rem',
    marginBottom: '0rem',
  },
}));

export default CustomerPrime100AccountProfileWithRenewalChanges;
